import { FormatNumber } from '@components/formatNumber';
import { cn } from '@utils/helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBarStyle } from './helpers';
import type { BarRowData, HorizontalBarChartProps } from './types';

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ data, language = 'de' }) => {
  const [initial, setInitial] = useState(true);
  const initialRef = useRef<boolean>(true);
  const nodeRef = useRef<HTMLDivElement>(null);

  const checkIfInViewport = useCallback(() => {
    if (!initialRef.current || !nodeRef.current) {
      return;
    }

    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const rect = nodeRef.current.getBoundingClientRect();
    const edge = nodeRef.current.offsetHeight / 2;

    if (viewportHeight - rect.top >= edge) {
      initialRef.current = false;
      setInitial(false);
      document.removeEventListener('scroll', checkIfInViewport);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', checkIfInViewport);
    checkIfInViewport();
    return () => document.removeEventListener('scroll', checkIfInViewport);
  }, [checkIfInViewport]);

  const renderRow = (row: BarRowData, index: number) => {
    const val = Math.abs(Math.round(row.percent * 10000) / 100);
    const visibleForSection = row.percent < 0 ? 'negative' : 'positive';

    const barClass = (section: 'negative' | 'positive') =>
      cn(
        'horizontal-bar-chart-bar',
        section,
        { initial },
        {
          'no-overflow': !(section === visibleForSection && val > 100),
        },
      );

    return (
      <div key={`row-${row.name || index}`} className="flex justify-between items-center border-b border-[#e2e2e2] h-[30px] min-h-[30px] transition-[height] duration-1000 ease-in-out max-[530px]:h-auto">
        <div className="min-w-[200px] max-w-[200px] text-black transition-[min-width,max-width] duration-1000 ease-in-out max-[530px]:min-w-[110px] max-[530px]:max-w-[110px]">{row.name}</div>
        <div className={barClass('negative')} style={getBarStyle({ section: 'negative', value: row.percent, color: row.color })} />
        <div className={barClass('positive')} style={getBarStyle({ section: 'positive', value: row.percent, color: row.color })} />
        <div className="min-w-[70px] max-w-[70px] text-right text-black">
          <FormatNumber suffix="%" useLocale={language}>
            {row.percent * 100}
          </FormatNumber>
        </div>
      </div>
    );
  };

  return (
    <div ref={nodeRef} className="border-t border-[#e2e2e2] mb-4 -HorizontalBarChart -Wrapper">
      {data.map(renderRow)}
    </div>
  );
};
